import type {
  CommandBarAction,
  FrecencyStorage,
  ActionSection,
} from '../actions/types'

/**
 * Match types for search scoring, ordered by preference.
 */
export type MatchType =
  | 'exact'
  | 'starts'
  | 'boundary'
  | 'contains'
  | 'alias'
  | 'fuzzy'
  | 'none'

/**
 * Scoring weights for different match types.
 */
const MATCH_SCORES: Record<MatchType, number> = {
  exact: 100,
  starts: 80,
  boundary: 60,
  contains: 40,
  alias: 30,
  fuzzy: 10,
  none: 0,
}

/**
 * Multiplier for frecency boost (frecency score * this value is added to match score).
 */
const FRECENCY_BOOST_MULTIPLIER = 10

/**
 * Section-specific score boosts to prioritize certain result types.
 * Channels are boosted to appear above other similarly-scored results.
 */
const SECTION_BOOST: Partial<Record<ActionSection, number>> = {
  channels: 15,
}

/**
 * Result of searching an action.
 */
export interface SearchResult {
  action: CommandBarAction
  score: number
  matchType: MatchType
}

/**
 * Calculate Levenshtein distance between two strings.
 * Returns the minimum number of single-character edits needed to transform s1 into s2.
 */
export function levenshteinDistance(s1: string, s2: string): number {
  const len1 = s1.length
  const len2 = s2.length

  // Quick returns for edge cases
  if (len1 === 0) return len2
  if (len2 === 0) return len1

  // Create two rows for the DP matrix (space optimization)
  let prevRow = Array.from({ length: len2 + 1 }, (_, i) => i)
  let currRow: number[] = []
  currRow.length = len2 + 1

  for (let i = 1; i <= len1; i++) {
    currRow[0] = i
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1
      currRow[j] = Math.min(
        prevRow[j] + 1, // deletion
        currRow[j - 1] + 1, // insertion
        prevRow[j - 1] + cost, // substitution
      )
    }
    ;[prevRow, currRow] = [currRow, prevRow]
  }

  return prevRow[len2]
}

/**
 * Check if query matches words with fuzzy matching (Levenshtein distance <= 1).
 * @param query - The search query (should be lowercase)
 * @param words - Pre-computed lowercase words from the text
 */
function isFuzzyMatch(query: string, words: string[]): boolean {
  // Only apply fuzzy matching for queries of 4+ characters
  if (query.length < 4) return false

  for (const word of words) {
    if (word.length >= query.length - 1 && word.length <= query.length + 1) {
      const distance = levenshteinDistance(query, word)
      if (distance <= 1) return true
    }
  }

  return false
}

/**
 * Check if query matches at a word boundary.
 * Example: "post" matches words ["create", "post"] at word boundary.
 * @param query - The search query (should be lowercase)
 * @param words - Pre-computed lowercase words from the text
 */
function isWordBoundaryMatch(query: string, words: string[]): boolean {
  return words.some((word) => word.startsWith(query))
}

/**
 * Calculate the match score for a query against a text and optional aliases.
 * @param normalizedQuery - The search query, already lowercased and trimmed
 * @param text - The text to match against (e.g., displayName)
 * @param searchTokens - Pre-computed lowercase words from text for optimized matching
 * @param aliases - Optional aliases for the text
 */
export function calculateMatchScore(
  normalizedQuery: string,
  text: string,
  searchTokens: string[],
  aliases: string[] = [],
): { score: number; matchType: MatchType } {
  const normalizedText = text.toLowerCase()

  if (!normalizedQuery) {
    return { score: 0, matchType: 'none' }
  }

  // Exact match
  if (normalizedText === normalizedQuery) {
    return { score: MATCH_SCORES.exact, matchType: 'exact' }
  }

  // Starts with match
  if (normalizedText.startsWith(normalizedQuery)) {
    return { score: MATCH_SCORES.starts, matchType: 'starts' }
  }

  // Word boundary match (uses pre-computed tokens)
  if (isWordBoundaryMatch(normalizedQuery, searchTokens)) {
    return { score: MATCH_SCORES.boundary, matchType: 'boundary' }
  }

  // Contains match
  if (normalizedText.includes(normalizedQuery)) {
    return { score: MATCH_SCORES.contains, matchType: 'contains' }
  }

  // Alias match
  for (const alias of aliases) {
    const normalizedAlias = alias.toLowerCase()
    if (
      normalizedAlias === normalizedQuery ||
      normalizedAlias.startsWith(normalizedQuery)
    ) {
      return { score: MATCH_SCORES.alias, matchType: 'alias' }
    }
  }

  // Fuzzy match (uses pre-computed tokens)
  if (isFuzzyMatch(normalizedQuery, searchTokens)) {
    return { score: MATCH_SCORES.fuzzy, matchType: 'fuzzy' }
  }

  return { score: 0, matchType: 'none' }
}

/**
 * Calculate frecency boost from stored frecency data.
 * Returns a normalized score between 0 and 1.
 */
function calculateFrecencyBoost(
  actionId: string,
  frecencyData: FrecencyStorage,
): number {
  const data = frecencyData[actionId]
  if (!data) return 0

  const now = Date.now()
  const daysSinceLastUse = (now - data.lastUsed) / (1000 * 60 * 60 * 24)

  // Frequency score: log10(useCount + 1), normalized to 0-1 range
  const frequencyScore = Math.min(Math.log10(data.useCount + 1) / 2, 1)

  // Recency score: exponential decay with 30-day half-life
  const recencyScore = Math.pow(0.5, daysSinceLastUse / 30)

  // Momentum: boost if used recently multiple times
  const recentUsesInLast6Hours = data.recentUses.filter(
    (ts) => now - ts < 6 * 60 * 60 * 1000,
  ).length
  const momentumBonus = recentUsesInLast6Hours >= 2 ? 1.5 : 1.0

  return frequencyScore * recencyScore * momentumBonus
}

/**
 * Search actions with weighted scoring algorithm.
 *
 * Scoring formula: final_score = base_score + (frecency_boost * 10)
 *
 * Returns actions sorted by score in descending order.
 * Actions with score 0 are filtered out (no match).
 */
export function searchActions(
  query: string,
  actions: CommandBarAction[],
  frecencyData: FrecencyStorage = {},
): SearchResult[] {
  const normalizedQuery = query.toLowerCase().trim()

  if (!normalizedQuery) {
    // No query - return all actions with frecency-based ordering
    return actions
      .map((action) => {
        const frecencyBoost = calculateFrecencyBoost(action.id, frecencyData)
        return {
          action,
          score: frecencyBoost * FRECENCY_BOOST_MULTIPLIER,
          matchType: 'none' as const,
        }
      })
      .toSorted((a, b) => b.score - a.score)
  }

  const results: SearchResult[] = []

  for (const action of actions) {
    // Check displayName match (uses pre-computed tokens)
    const nameMatch = calculateMatchScore(
      normalizedQuery,
      action.displayName,
      action.searchTokens,
    )

    // Check keywords match (keywords are typically single words, tokenize inline)
    let keywordMatch: { score: number; matchType: MatchType } = {
      score: 0,
      matchType: 'none',
    }
    for (const keyword of action.keywords) {
      const keywordTokens = keyword.toLowerCase().split(/\s+/)
      const match = calculateMatchScore(normalizedQuery, keyword, keywordTokens)
      if (match.score > keywordMatch.score) {
        keywordMatch = match
      }
    }

    // Use the best match
    const bestMatch =
      nameMatch.score >= keywordMatch.score ? nameMatch : keywordMatch

    if (bestMatch.score > 0) {
      const frecencyBoost = calculateFrecencyBoost(action.id, frecencyData)
      const sectionBoost = SECTION_BOOST[action.section] ?? 0
      const finalScore =
        bestMatch.score +
        frecencyBoost * FRECENCY_BOOST_MULTIPLIER +
        sectionBoost

      results.push({
        action,
        score: finalScore,
        matchType: bestMatch.matchType,
      })
    }
  }

  // Sort by score descending
  return results.toSorted((a, b) => b.score - a.score)
}

/**
 * Sections that are hidden by default and only shown when query matches.
 * These sections only appear when the user actively searches for them.
 */
const HIDDEN_BY_DEFAULT_SECTIONS: ReadonlySet<ActionSection> = new Set([
  'help',
  'tags',
])

/**
 * Check if a section should be visible based on the query.
 * Some sections are hidden by default and only shown when explicitly searched for.
 */
function shouldShowSection(section: ActionSection, hasQuery: boolean): boolean {
  if (!HIDDEN_BY_DEFAULT_SECTIONS.has(section)) {
    return true
  }
  // Hidden sections are only shown when there's a query that matches
  return hasQuery
}

/**
 * Filter and score actions for display, maintaining section grouping.
 *
 * This is the main entry point for the command bar search.
 * It filters actions based on query, applies scoring, and returns
 * actions ready for display (with section preserved).
 *
 * Visibility rules:
 * - Help and Tags sections are hidden by default (no query)
 * - They appear only when the search query matches an action in those sections
 */
export function filterAndScoreActions(
  query: string,
  actions: CommandBarAction[],
  frecencyData: FrecencyStorage = {},
): CommandBarAction[] {
  const normalizedQuery = query.trim()
  const hasQuery = normalizedQuery.length > 0

  // If no query, filter out hidden-by-default sections
  if (!hasQuery) {
    const visibleActions = actions.filter((action) =>
      shouldShowSection(action.section, false),
    )
    return visibleActions
  }

  // With query, let searchActions filter and score
  const searchResults = searchActions(normalizedQuery, actions, frecencyData)

  // Return actions in score order
  return searchResults.map((result) => result.action)
}
